import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../../lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = {
    tag: "",
    title: "",
    body: "",
    href: "",
    date: new Date().toISOString().slice(0, 10),
};

export default function TrainingsAdmin() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => api.get("/admin/trainings").then((r) => setItems(r.data)), []);
    useEffect(() => {
        load();
    }, [load]);

    const openNew = () => {
        setForm(empty);
        setEditing({ id: null });
        setError("");
    };
    const openEdit = (t) => {
        setForm({
            tag: t.tag,
            title: t.title,
            body: t.body,
            href: t.href || "",
            date: t.date,
        });
        setEditing(t);
        setError("");
    };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            if (editing.id) await api.put(`/admin/trainings/${editing.id}`, form);
            else await api.post("/admin/trainings", form);
            setEditing(null);
            await load();
        } catch (err) {
            setError(apiError(err.response?.data?.detail) || "Could not save");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Delete "${t.title}"?`)) return;
        await api.delete(`/admin/trainings/${t.id}`);
        load();
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const isPast = (d) => d < new Date().toISOString().slice(0, 10);

    return (
        <div data-testid="admin-trainings">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-serif text-3xl text-blue-50">Trainings</h1>
                    <p className="mt-1 text-sm text-slate-300/55">
                        CE trainings. Past-dated trainings are hidden from the
                        public site automatically.
                    </p>
                </div>
                <button
                    data-testid="training-new-button"
                    onClick={openNew}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-5 py-2.5 text-sm font-semibold text-[#ffffff] hover:brightness-110"
                >
                    <Plus className="h-4 w-4" /> New training
                </button>
            </div>

            <div className="mt-8 space-y-3">
                {items.map((t) => (
                    <div
                        key={t.id}
                        className="flex items-center gap-4 rounded-2xl border border-blue-500/15 bg-[#0c172e] p-4"
                    >
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <span className="text-xs uppercase tracking-wide text-blue-400/80">
                                    {t.tag}
                                </span>
                                {isPast(t.date) && (
                                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-300/50">
                                        Past · hidden
                                    </span>
                                )}
                            </div>
                            <h3 className="font-serif text-lg text-slate-300">
                                {t.title}
                            </h3>
                            <p className="text-xs text-slate-300/45">{t.date}</p>
                        </div>
                        <button
                            onClick={() => openEdit(t)}
                            aria-label="Edit"
                            data-testid={`training-edit-${t.id}`}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400/80 hover:bg-blue-500/15"
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => remove(t)}
                            aria-label="Delete"
                            data-testid={`training-delete-${t.id}`}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-red-300/80 hover:bg-red-500/15"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-6">
                    <form
                        onSubmit={save}
                        className="my-6 w-full max-w-xl rounded-2xl border border-blue-500/20 bg-[#0c172e] p-7"
                    >
                        <div className="flex items-center justify-between">
                            <h2 className="font-serif text-2xl text-blue-50">
                                {editing.id ? "Edit training" : "New training"}
                            </h2>
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400/70 hover:bg-blue-500/15"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="mt-6 space-y-5">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                        Tag
                                    </label>
                                    <input
                                        data-testid="training-tag-input"
                                        required
                                        value={form.tag}
                                        onChange={set("tag")}
                                        placeholder="EMDR & Polyvagal"
                                        className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                        Date
                                    </label>
                                    <input
                                        data-testid="training-date-input"
                                        type="date"
                                        required
                                        value={form.date}
                                        onChange={set("date")}
                                        className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                    Title
                                </label>
                                <input
                                    data-testid="training-title-input"
                                    required
                                    value={form.title}
                                    onChange={set("title")}
                                    className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                    Description
                                </label>
                                <textarea
                                    rows={3}
                                    required
                                    value={form.body}
                                    onChange={set("body")}
                                    className="w-full resize-none rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-slate-400/50">
                                    Registration link
                                </label>
                                <input
                                    value={form.href}
                                    onChange={set("href")}
                                    placeholder="https://…"
                                    className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] px-4 py-3 text-sm text-blue-50 outline-none focus:border-blue-400"
                                />
                            </div>
                            {error && <p className="text-sm text-red-400">{error}</p>}
                        </div>
                        <div className="mt-7 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="rounded-full border border-blue-500/30 px-6 py-2.5 text-sm text-slate-300 hover:bg-blue-500/10"
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="training-save-button"
                                type="submit"
                                disabled={busy}
                                className="rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-6 py-2.5 text-sm font-semibold text-[#ffffff] hover:brightness-110 disabled:opacity-60"
                            >
                                {busy ? "Saving…" : "Save training"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
